import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import ChatPage from "../../pages/ChatPage";
import { useAuth } from "../../contexts/AuthContext";
import { upsertPushSubscription } from "../../api/nudges";
import { supabase } from "../../lib/supabase";
import { APP_SHELL_TAGLINE } from "../../lib/albisVoice";
import { guestAuthLoginHref } from "../../lib/guestAcquisition";
import { trackKitaReminderTap } from "../../lib/kitaReminderAnalytics";
import { trackBriefingPushTap } from "../../lib/briefingAnalytics";
import InstallHomeScreenPrompt from "../InstallHomeScreenPrompt/InstallHomeScreenPrompt";
import PushStandalonePrompt from "../PushStandalonePrompt/PushStandalonePrompt";
import { flushPendingMutations } from "../../lib/flushPendingMutations";

export default function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, signOut } = useAuth();
  const showBottomNav = Boolean(user);
  const onChat = location.pathname === "/";
  const [chatAlive, setChatAlive] = useState(onChat);

  useEffect(() => {
    if (onChat) setChatAlive(true);
  }, [onChat]);

  useEffect(() => {
    if (!user?.id) return undefined;
    flushPendingMutations(user.id).catch(() => {});
    return undefined;
  }, [user?.id]);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const handleMessage = (event) => {
      if (event.data?.type !== "albis:navigate" || !event.data.url) return;
      try {
        const target = new URL(event.data.url, window.location.origin);
        if (target.origin !== window.location.origin) return;
        trackKitaReminderTap({
          analytics: event.data.analytics,
          searchParams: target.searchParams,
        });
        trackBriefingPushTap({ analytics: event.data.analytics });
        navigate(`${target.pathname}${target.search}`);
      } catch {
        /* ignore malformed deep links */
      }
    };
    navigator.serviceWorker.addEventListener("message", handleMessage);
    return () => {
      navigator.serviceWorker.removeEventListener("message", handleMessage);
    };
  }, [navigate]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        let pushReady = false;
        if ("serviceWorker" in navigator && typeof Notification !== "undefined" && Notification.permission === "granted") {
          const reg = await navigator.serviceWorker.getRegistration();
          const sub = await reg?.pushManager?.getSubscription();
          if (sub && !cancelled) {
            pushReady = true;
            await upsertPushSubscription(sub);
          }
        }
        if (cancelled || pushReady || !user.user_metadata?.kita_email_opt_out_at) return;
        await supabase.auth.updateUser({ data: { kita_email_opt_out_at: null } });
      } catch {
        /* optional sync */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const handleAuthClick = () => {
    if (user) {
      signOut();
    } else {
      navigate(guestAuthLoginHref(location.pathname, location.search));
    }
  };

  return (
    <div
      className={`app-shell${showBottomNav ? "" : " app-shell--guest"}`}
    >
      <header className="app-shell-header">
        <div className="app-shell-brand">
          <Link to="/" className="app-shell-title-link">
            <h1 className="app-shell-title">Albis.Life</h1>
          </Link>
          <p className="app-shell-tagline">{APP_SHELL_TAGLINE}</p>
        </div>
        <div className="app-shell-header-actions">
          <button
            type="button"
            className="app-shell-auth-btn"
            onClick={handleAuthClick}
            aria-label={user ? "Abmelden" : "Anmelden"}
            disabled={loading}
          >
            {user ? "Abmelden" : "Anmelden"}
          </button>
        </div>
      </header>

      <main
        className={`app-shell-main${showBottomNav ? "" : " app-shell-main--no-nav"}`}
      >
        {chatAlive ? (
          <div className="app-shell-chat" hidden={!onChat} inert={!onChat ? true : undefined} aria-hidden={!onChat}>
            <ChatPage />
          </div>
        ) : null}
        <Outlet />
      </main>

      {showBottomNav ? (
        <nav className="app-shell-bottom-nav" aria-label="Hauptnavigation">
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              `app-shell-nav-item app-shell-nav-item--primary ${isActive ? "active" : ""}`
            }
            aria-label="Albis"
          >
            <span className="app-shell-nav-icon app-shell-nav-icon--primary">💬</span>
            <span className="app-shell-nav-label">Albis</span>
          </NavLink>
          <NavLink
            to="/mehr"
            className={({ isActive }) =>
              `app-shell-nav-item ${isActive ? "active" : ""}`
            }
            aria-label="Mehr"
          >
            <span className="app-shell-nav-icon">⋯</span>
            <span className="app-shell-nav-label">Mehr</span>
          </NavLink>
        </nav>
      ) : null}

      <InstallHomeScreenPrompt />
      <PushStandalonePrompt />
    </div>
  );
}
